import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axiosInstance";

interface CreateUserAccountTabProps {
  memberId: number;
  memberName?: string;
  memberPhone?: string;
}

const CreateUserAccountTab: React.FC<CreateUserAccountTabProps> = ({
  memberId,
  memberName,
  memberPhone,
}) => {
  const [formData, setFormData] = useState<any>({
    user_name: "",
    password: "",
    role_id: null, // Use role_id instead of role
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accountExists, setAccountExists] = useState(false);
  const [accountData, setAccountData] = useState<any>(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPasswordUpdate, setShowPasswordUpdate] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [justCreatedAccount, setJustCreatedAccount] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const fetchAccountData = useCallback(async () => {
    if (!memberId) return;
    
    setLoadingAccount(true);
    try {
      const resp: any = await axiosInstance.get(`/api/accounts/${memberId}`);
      if (resp?.status === 200 && resp?.data?.status === "ok") {
        setAccountExists(true);
        setAccountData(resp.data.data);
        // Prefill form with existing account data
        setFormData({
          name: resp.data.data.name || memberName || "",
          user_name: resp.data.data.user_name || "",
          password: "", // Don't prefill password for security
          role_id: resp.data.data.roleEntity?.id || null,
          role: resp.data.data.role || null, // Keep for display purposes
          block: resp.data.data.block || false,
        });
      } else if (resp?.data?.status === "error") {
        // Account not found - this is expected behavior
        setAccountExists(false);
        setAccountData(null);
      } else {
        setAccountExists(false);
        setAccountData(null);
      }
    } catch (error: any) {
      // If account not found, that's expected - member doesn't have an account yet
      if (error?.response?.data?.status === "error" || error?.response?.status === 404) {
        setAccountExists(false);
        setAccountData(null);
      } else {
        console.error("Error fetching account data:", error);
        // Don't show error toast for "not found" - it's expected
        if (error?.response?.status !== 404) {
          toast.error("Error checking account status");
        }
      }
    } finally {
      setLoadingAccount(false);
    }
  }, [memberId]);

  useEffect(() => {
    setJustCreatedAccount(false);
    fetchAccountData();
  }, [fetchAccountData]);

  const handleChange = (key: string, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  const validateEmail = (email: string) => {
    // Only validate email format if it contains "@", otherwise allow as username
    if (email.includes("@")) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
    // If no "@", it's a username - allow it (just check it's not empty)
    return email.trim().length > 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Avoid duplicate submission (e.g. double-click or strict mode)
    if (loading) return;

    // Prevent submission if account already exists (no toast - UI already shows the alert)
    if (accountExists) return;

    if (!formData?.user_name || !formData.user_name.trim()) {
      toast.error("Email/Username is required");
      return;
    }

    if (!validateEmail(formData?.user_name)) {
      // Check if it contains @ to determine error message
      if (formData.user_name.includes("@")) {
        toast.error("Invalid email address");
      } else {
        toast.error("Username cannot be empty");
      }
      return;
    }

    // Only validate password if creating new account
    if (!accountExists) {
      if (!formData?.password) {
        toast.error("Password is required");
        return;
      }

      if (formData?.password.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
    }

    setLoading(true);
    try {
      const requestData: any = {
        name: memberName || "User",
        user_name: formData.user_name,
        password: formData.password,
        role: 1, // Default legacy role for non-Super Admin
      };
      if (formData.role_id != null && formData.role_id !== "") {
        requestData.role_id = Number(formData.role_id);
      }

      // Only include member_id if it exists
      if (memberId) {
        requestData.member_id = memberId;
      }

      const resp: any = await axiosInstance.post(`/api/auth`, requestData);
      
      // Check if response indicates success
      if (resp.status === 200 || resp.status === 201) {
        // Check response body for error status
        if (resp.data?.status === "error") {
          toast.error(resp.data?.message || "Failed to create user account");
          return;
        }
        if (resp.data?.status === "ok") {
          setJustCreatedAccount(true);
          toast.success("User account created successfully");
          // Refresh account data to show the newly created account
          await fetchAccountData();
          // After 4s show the normal "Account already exists" banner text
          setTimeout(() => setJustCreatedAccount(false), 4000);
        }
      }
    } catch (error: any) {
      console.error(error);
      // Check multiple possible error response formats
      const errorMessage = 
        error?.response?.data?.message ||
        error?.response?.data?.body ||
        error?.message ||
        "Failed to create user account. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData?.name) {
      toast.error("Name is required");
      return;
    }

    if (!formData?.user_name || !formData.user_name.trim()) {
      toast.error("Email/Username is required");
      return;
    }

    if (!validateEmail(formData?.user_name)) {
      // Check if it contains @ to determine error message
      if (formData.user_name.includes("@")) {
        toast.error("Invalid email address");
      } else {
        toast.error("Username cannot be empty");
      }
      return;
    }

    setLoading(true);
    try {
      const requestData: any = {
        name: formData.name,
        user_name: formData.user_name,
        block: formData.block || false,
        member_id: memberId,
      };

      // Only include password if it's provided
      if (formData.password) {
        requestData.password = formData.password;
      }

      // Only include role_id if it's been set (optional)
      if (formData.role_id !== undefined && formData.role_id !== null && formData.role_id !== "") {
        requestData.role_id = Number(formData.role_id);
      }

      console.log("Update account request data:", requestData);

      const resp: any = await axiosInstance.put(`/api/auth/${accountData.id}`, requestData);
      
      if (resp.status === 200) {
        toast.success("Account updated successfully");
        setIsEditMode(false);
        await fetchAccountData();
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.body ||
        error?.response?.data?.message ||
        "Failed to update account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword) {
      toast.error("Password is required");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setUpdatingPassword(true);
    try {
      const resp: any = await axiosInstance.put(`/api/auth/${accountData.id}/password`, {
        password: newPassword,
      });
      
      if (resp.status === 200) {
        toast.success("Password updated successfully");
        setNewPassword("");
        setShowPasswordUpdate(false);
        await fetchAccountData();
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.body ||
        error?.response?.data?.message ||
        "Failed to update password. Please try again."
      );
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleBlockUnblock = async (block: boolean) => {
    setLoading(true);
    try {
      const resp: any = await axiosInstance.put(`/api/auth/${accountData.id}/block`, {
        block: block,
      });
      
      if (resp.status === 200) {
        toast.success(`Account ${block ? "blocked" : "unblocked"} successfully`);
        await fetchAccountData();
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.body ||
        error?.response?.data?.message ||
        `Failed to ${block ? "block" : "unblock"} account. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const resp: any = await axiosInstance.delete(`/api/auth/${accountData.id}`);
      
      if (resp.status === 200) {
        toast.success("Account deleted successfully");
        setConfirmDelete(false);
        await fetchAccountData();
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.body ||
        error?.response?.data?.message ||
        "Failed to delete account. Please try again."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <i className="fa-solid fa-user-plus text-blue-600 dark:text-blue-400"></i>
          {accountExists ? "User Account" : "Create User Account"}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {accountExists
            ? `This member already has a login account. Account details are shown below.`
            : `Create a login account for ${memberName || "this member"}. This will allow them to access the system.`}
        </p>
      </div>

      {/* Account Exists Alert */}
      {accountExists && (
        <div className={`mb-6 rounded-lg p-4 border ${
          accountData?.block 
            ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
            : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {accountData?.block ? (
                <i className="fa-solid fa-ban text-red-600 dark:text-red-400"></i>
              ) : (
                <i className="fa-solid fa-check-circle text-green-600 dark:text-green-400"></i>
              )}
              <span className={`text-sm font-semibold ${
                accountData?.block 
                  ? "text-red-800 dark:text-red-300"
                  : "text-green-800 dark:text-green-300"
              }`}>
                {accountData?.block ? "Account is blocked" : justCreatedAccount ? "Account created successfully" : "Account found for this member. You may update it anytime."}
              </span>
            </div>
            {!isEditMode && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditMode(true)}
                  className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
                >
                  <i className="fa-solid fa-edit"></i>
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loadingAccount && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading account information...</span>
        </div>
      )}

      {!loadingAccount && (
        <form className="space-y-6" onSubmit={accountExists && isEditMode ? handleUpdateAccount : handleSubmit}>
        {/* Member Information Display */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Member Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Name
              </div>
              <div className="text-base font-medium text-gray-900 dark:text-white">
                {memberName || "-"}
              </div>
            </div>
            {memberPhone && (
              <div>
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Phone
                </div>
                <div className="text-base font-medium text-gray-900 dark:text-white">
                  {memberPhone}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Name */}
        {accountExists && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name={`update-account-name-${memberId}`}
              value={formData?.name || ""}
              onChange={(e: any) => handleChange("name", e?.target.value)}
              placeholder="Enter name"
              className="dark-input w-full px-4 py-2.5"
              required
              disabled={!isEditMode}
              autoComplete="off"
            />
          </div>
        )}

        {/* Email/Username */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Email/Username <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name={`create-account-username-${memberId}`}
            value={formData?.user_name || ""}
            onChange={(e: any) => handleChange("user_name", e?.target.value)}
            placeholder="Enter email address or username"
            className="dark-input w-full px-4 py-2.5"
            required
            disabled={accountExists && !isEditMode}
            autoComplete="off"
          />
          {accountExists && !isEditMode && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Click Edit to modify account details
            </p>
          )}
        </div>

        {/* Password */}
        {!accountExists && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name={`create-account-password-${memberId}`}
                value={formData?.password || ""}
                onChange={(e: any) => handleChange("password", e?.target.value)}
                placeholder="Enter password (minimum 6 characters)"
                className="dark-input w-full px-4 py-2.5 pr-10"
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={handleTogglePassword}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 p-1"
              >
                {showPassword ? (
                  <i className="fa-solid fa-eye-slash"></i>
                ) : (
                  <i className="fa-solid fa-eye"></i>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Role Management hidden on member User Account page - role is optional */}

        {/* Password Update Section for Existing Accounts */}
        {accountExists && isEditMode && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Update Password <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <input
              type="text"
              name={`update-account-password-${memberId}`}
              value={formData?.password || ""}
              onChange={(e: any) => handleChange("password", e?.target.value)}
              placeholder="Leave empty to keep current password"
              className="dark-input w-full px-4 py-2.5"
              autoComplete="new-password"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Enter new password to update, or leave empty to keep current password
            </p>
          </div>
        )}

        {/* Separate Password Update Section */}
        {accountExists && !isEditMode && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Update Password
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Change the account password
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPasswordUpdate(!showPasswordUpdate)}
                className="btn-primary px-4 py-2 text-sm"
              >
                {showPasswordUpdate ? "Cancel" : "Change Password"}
              </button>
            </div>
            {showPasswordUpdate && (
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name={`change-password-new-${memberId}`}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (minimum 6 characters)"
                      className="dark-input w-full px-4 py-2.5 pr-10"
                      required
                      minLength={6}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={handleTogglePassword}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 p-1"
                    >
                      {showPassword ? (
                        <i className="fa-solid fa-eye-slash"></i>
                      ) : (
                        <i className="fa-solid fa-eye"></i>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordUpdate(false);
                      setNewPassword("");
                    }}
                    className="btn-primary px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updatingPassword}
                    className="btn-primary px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {updatingPassword ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-key"></i>
                        <span>Update Password</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          {accountExists ? (
            isEditMode ? (
              <div className="flex items-center justify-between gap-4">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditMode(false);
                      // Reset form to original data
                      setFormData({
                        name: accountData?.name || memberName || "",
                        user_name: accountData?.user_name || "",
                        password: "",
                        role_id: accountData?.roleEntity?.id || null,
                        role: accountData?.role || null,
                        block: accountData?.block || false,
                      });
                    }}
                    className="btn-primary px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdateAccount}
                    disabled={loading}
                    className="btn-primary px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-save"></i>
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleBlockUnblock(!accountData?.block)}
                    disabled={loading}
                    className={`px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50 ${
                      accountData?.block
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-red-600 hover:bg-red-700 text-white"
                    } rounded-lg`}
                  >
                    <i className={`fa-solid ${accountData?.block ? "fa-unlock" : "fa-ban"}`}></i>
                    <span>{accountData?.block ? "Unblock" : "Block"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    disabled={loading || deleting}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50 rounded-lg"
                  >
                    <i className="fa-solid fa-trash"></i>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => handleBlockUnblock(!accountData?.block)}
                  disabled={loading}
                  className={`px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50 ${
                    accountData?.block
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-red-600 hover:bg-red-700 text-white"
                  } rounded-lg`}
                >
                  <i className={`fa-solid ${accountData?.block ? "fa-unlock" : "fa-ban"}`}></i>
                  <span>{accountData?.block ? "Unblock Account" : "Block Account"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  disabled={loading || deleting}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50 rounded-lg"
                >
                  <i className="fa-solid fa-trash"></i>
                  <span>Delete Account</span>
                </button>
              </div>
            )
          ) : (
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary px-6 py-2.5 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-user-plus"></i>
                    <span>Create Account</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </form>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setConfirmDelete(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirm Deletion
              </h2>
              <button
                onClick={() => setConfirmDelete(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete this account? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="btn-primary px-4 py-2"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-trash"></i>
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateUserAccountTab;

